import {
    Box,
    Button,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useContext, useState } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { isBrowser } from "react-device-detect";
import useFetch from "../../hooks/useFetch";
import { Loading } from "../../components/Loading";
import { ServerProblem } from "../ReRoute/ServerProblem";
import { UserContext } from "../../context/UserContext";
import { ReceivingFacility } from "../../interfaces/ReceivingFacility";
import ReceivingFacilityService from "../../services/ReceivingFacilityService";
import { ReceivingFacilityDialog } from "./ReceivingFacilityDialog";

export const ReceivingFacilities = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [cookies] = useCookies(["access_token"]);
    const userCtx = useContext(UserContext);
    const canEdit = userCtx?.user?.role?.roletype === "orgadmin";
    const fontVariant = isBrowser ? "body2" : "caption";

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selected, setSelected] = useState<ReceivingFacility | undefined>(undefined);

    const { data, loading, error, refresh } = useFetch({
        url: ReceivingFacilityService.listUrl(true),
        access_token: cookies.access_token,
    });

    const openDialog = (facility?: ReceivingFacility) => {
        setSelected(facility);
        setDialogOpen(true);
    };

    const handleSaved = () => {
        setDialogOpen(false);
        refresh();
    };

    if (error) return <ServerProblem />;
    if (loading || !data) return <Loading />;

    const facilities: ReceivingFacility[] = data;
    const cellSx = { padding: "16px 6px" };
    // one-line column names, so a narrow table does not wrap them (their size is set on the row below)
    const headerCellSx = { ...cellSx, whiteSpace: "nowrap" };

    const routable = (facility: ReceivingFacility) => {
        const covered = facility.routing?.covered;
        if (covered === true || covered === false) {
            const hint = covered
                ? t("rf_routable_yes_hint", { metres: facility.routing?.road_m })
                : t("rf_routable_no_hint");
            return (
                <Tooltip title={hint}>
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        {covered ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                            <CancelIcon color="error" fontSize="small" />
                        )}
                        <Typography variant={fontVariant}>
                            {t(covered ? "rf_routable_yes" : "rf_routable_no")}
                        </Typography>
                    </Box>
                </Tooltip>
            );
        }
        return (
            <Tooltip title={t("rf_routable_unknown_hint")}>
                <Typography variant={fontVariant}>–</Typography>
            </Tooltip>
        );
    };

    return (
        <Box sx={{ px: 3, py: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, px: 1 }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                    {t("receiving_facilities")}
                </Typography>
                {canEdit && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>
                        {t("rf_add_facility")}
                    </Button>
                )}
            </Box>

            {/* a container, so the address can be measured against the table's width (cqi) */}
            <TableContainer component={Paper} sx={{ containerType: "inline-size" }}>
                <Table aria-label={t("receiving_facilities")}>
                    <TableHead>
                        <TableRow
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                "& th": { color: theme.palette.text.secondary },
                                // the body text's size: the app theme's 1.25rem for table heads wraps these
                                "& th.MuiTableCell-root": { fontSize: "0.875rem" },
                            }}
                        >
                            <TableCell sx={headerCellSx}>{t("rf_name")}</TableCell>
                            <TableCell sx={headerCellSx}>{t("rf_address")}</TableCell>
                            <TableCell sx={headerCellSx}>{t("rf_routable")}</TableCell>
                            <TableCell sx={headerCellSx}>{t("rf_notification_methods")}</TableCell>
                            <TableCell sx={headerCellSx}>{t("rf_departments")}</TableCell>
                            <TableCell sx={headerCellSx}>{t("rf_contacts")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {facilities.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant={fontVariant}>{t("rf_no_facilities")}</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {facilities.map((facility) => {
                            return (
                                <TableRow
                                    key={facility.id}
                                    hover
                                    onClick={() => openDialog(facility)}
                                    sx={{
                                        cursor: "pointer",
                                        "&:nth-of-type(odd)": { background: theme.palette.info.dark },
                                        "&:nth-of-type(even)": { background: theme.palette.info.light },
                                    }}
                                >
                                    <TableCell sx={cellSx}>
                                        <Typography variant={fontVariant}>{facility.name}</Typography>
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {facility.address_verified && (
                                                <CheckCircleIcon color="success" fontSize="small" />
                                            )}
                                            {/* at most half the table, wrapping beyond that */}
                                            <Typography
                                                variant={fontVariant}
                                                sx={{ maxWidth: "50cqi", overflowWrap: "anywhere" }}
                                            >
                                                {facility.display_address || facility.address}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={cellSx}>{routable(facility)}</TableCell>
                                    <TableCell sx={cellSx}>
                                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                            {facility.notification_methods.map((method) => (
                                                <Chip key={method} size="small" label={t(`rf_method_${method}`)} />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        <Typography variant={fontVariant}>
                                            {facility.departments.map((department) => department.name).join(", ") || "-"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        <Typography variant={fontVariant}>
                                            {facility.departments.reduce((count, department) => count + department.contacts.length, 0)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {dialogOpen && (
                <ReceivingFacilityDialog
                    open={dialogOpen}
                    facility={selected}
                    readOnly={!canEdit}
                    onClose={() => setDialogOpen(false)}
                    onSaved={handleSaved}
                />
            )}
        </Box>
    );
};
